import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import barcelonaPhoto from '../../Assets/images/beyond-barcelona.webp';
import fishingPhoto from '../../Assets/images/beyond-fishing.webp';
import goosePhoto from '../../Assets/images/beyond-goose.webp';
import portrait from '../../Assets/images/profile-hero.webp';
import './Home.scss';

type ExperienceRole = {
  period: string;
  role: string;
  scope: string;
  highlights: string[];
};

type ImpactStory = {
  category: string;
  description: string;
  metric: string;
  metricLabel: string;
  number: string;
  tags: string[];
  title: string;
};

const experienceRoles: ExperienceRole[] = [
  {
    period: '2024 — Present',
    role: 'Senior Software Engineer',
    scope: 'Lead engineer for Seagate’s Lyve Provisioning Tool',
    highlights: [
      'Redesigned a complex provisioning workflow as a focused single-page application.',
      'Led the Vue 3 and Vite migration, strengthened TypeScript boundaries, and coordinated releases across four global fulfillment sites.',
      'Created a regular feedback loop with fulfillment engineers to turn operational friction into targeted improvements.',
    ],
  },
  {
    period: '2020 — 2024',
    role: 'Software Engineer II',
    scope: 'Provisioning automation and customer-facing device management',
    highlights: [
      'Built order-driven provisioning, recovery workflows, and an auditable device-action history.',
      'Extended secure offline device access and connected web, Electron, Node.js, and native C++ capabilities.',
    ],
  },
  {
    period: '2019 — 2020',
    role: 'Software Engineer I',
    scope: 'Desktop delivery, testing, and product demonstrations',
    highlights: [
      'Shipped signed Electron applications with automatic updates and developed software demonstrated at CES 2020.',
      'Added Jest coverage and hardware-backed CI checks for security-sensitive product changes.',
    ],
  },
];

const impactStories: ImpactStory[] = [
  {
    category: 'Workflow design',
    description: 'Reframed a dense operational process into a single-page workflow that made high-volume provisioning faster to learn and easier to operate.',
    metric: '60%',
    metricLabel: 'fewer workflow clicks',
    number: '01',
    tags: ['Vue 3', 'Vite', 'TypeScript'],
    title: 'Simpler provisioning at global scale',
  },
  {
    category: 'Automation & reliability',
    description: 'Connected fulfillment software to order APIs, introduced recovery paths, and replaced opaque reset procedures with observable automation.',
    metric: '48h',
    metricLabel: 'of reprocessing avoided',
    number: '02',
    tags: ['Node.js', 'REST APIs', 'Telemetry'],
    title: 'Resilient device lifecycle automation',
  },
  {
    category: 'Secure device access',
    description: 'Improved device discovery and designed durable authenticated access for customers working in disconnected environments.',
    metric: '90%',
    metricLabel: 'faster device interrogation',
    number: '03',
    tags: ['Electron', 'Auth0', 'C++ bindings'],
    title: 'Fast, dependable desktop tooling',
  },
];

function ArrowIcon() {
  return <span aria-hidden="true">↗</span>;
}

function SectionHeading({
  eyebrow,
  id,
  number,
  title,
}: {
  eyebrow: string;
  id: string;
  number: string;
  title: string;
}) {
  return (
    <div className="section-heading">
      <span className="section-number" aria-hidden="true">{number}</span>
      <div>
        <p className="section-eyebrow">{eyebrow}</p>
        <h2 id={id}>{title}</h2>
      </div>
    </div>
  );
}

function ExperienceTimeline() {
  return (
    <div className="experience-timeline">
      {experienceRoles.map((item) => (
        <article className="experience-role" key={item.role}>
          <p className="experience-period">{item.period}</p>
          <div>
            <h3>{item.role}</h3>
            <p className="experience-scope">{item.scope}</p>
            <ul>
              {item.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
          </div>
        </article>
      ))}
    </div>
  );
}

function ImpactCard({ story }: { story: ImpactStory }) {
  return (
    <article className="impact-card">
      <div className="impact-topline">
        <span>{story.category}</span>
        <span aria-hidden="true">{story.number}</span>
      </div>
      <div className="impact-metric">
        <strong>{story.metric}</strong>
        <span>{story.metricLabel}</span>
      </div>
      <h3>{story.title}</h3>
      <p>{story.description}</p>
      <ul className="tag-list" aria-label={`${story.title} technologies`}>
        {story.tags.map((tag) => <li key={tag}>{tag}</li>)}
      </ul>
    </article>
  );
}

function Home() {
  const { t } = useTranslation();

  useEffect(() => {
    const sectionId = window.location.hash.slice(1);
    if (!sectionId) return;

    const target = document.getElementById(sectionId);
    if (target && typeof target.scrollIntoView === 'function') {
      window.requestAnimationFrame(() => target.scrollIntoView({ behavior: 'auto' }));
    }
  }, []);

  return (
    <>
      <main className="home">
        <section id="home" className="hero-section" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="hero-eyebrow">{t('home.eyebrow')}</p>
            <h1 id="hero-title">
              {t('home.title')}
              {' '}
              <span>{t('home.title-accent')}</span>
            </h1>
            <p className="hero-lead">{t('home.introduction')}</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#impact">
                {t('cta.impact')} <span aria-hidden="true">↓</span>
              </a>
              <a className="button button-secondary" href="mailto:jasonjconci@gmail.com">
                {t('cta.contact')} <ArrowIcon />
              </a>
            </div>
            <p className="hero-availability">
              <span aria-hidden="true" />
              {t('home.availability')}
            </p>
          </div>

          <div className="hero-visual" aria-label="Portrait of Jason Conci">
            <div className="portrait-offset" aria-hidden="true" />
            <div className="portrait-frame">
              <img src={portrait} alt="Jason Conci outdoors" />
            </div>
          </div>
        </section>

        <section id="about" className="site-section about-section" aria-labelledby="about-title">
          <SectionHeading
            eyebrow={t('intro.eyebrow')}
            id="about-title"
            number="01."
            title={t('intro.title')}
          />
          <div className="about-grid">
            <div className="about-copy">
              <p>{t('intro.about-me-1')}</p>
              <p>{t('intro.about-me-2')}</p>
            </div>
            <div className="capability-grid" aria-label="Core capabilities">
              <div>
                <span>01</span>
                <strong>Product &amp; workflow design</strong>
                <p>Clear interfaces and thoughtful tools shaped around the people who use them.</p>
              </div>
              <div>
                <span>02</span>
                <strong>Full-stack delivery</strong>
                <p>TypeScript, Vue, React, Electron, Node.js, Python, APIs, and data-rich services.</p>
              </div>
              <div>
                <span>03</span>
                <strong>Engineering leadership</strong>
                <p>Technical direction, pragmatic modernization, and reliable delivery across a team.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="experience" className="site-section experience-section" aria-labelledby="experience-title">
          <div className="experience-intro">
            <SectionHeading
              eyebrow={t('experience.eyebrow')}
              id="experience-title"
              number="02."
              title={t('experience.title')}
            />
            <div className="company-summary">
              <span>Seagate Technology</span>
              <strong>2019 — Present</strong>
              <p>{t('experience.description')}</p>
            </div>
          </div>
          <ExperienceTimeline />
        </section>

        <section id="impact" className="site-section impact-section" aria-labelledby="impact-title">
          <div className="impact-intro">
            <SectionHeading
              eyebrow={t('impact.eyebrow')}
              id="impact-title"
              number="03."
              title={t('impact.title')}
            />
            <p>{t('impact.description')}</p>
          </div>
          <div className="impact-grid">
            {impactStories.map((story) => <ImpactCard key={story.number} story={story} />)}
          </div>
        </section>

        <section id="profile" className="site-section profile-section" aria-labelledby="profile-title">
          <SectionHeading
            eyebrow={t('research.eyebrow')}
            id="profile-title"
            number="04."
            title={t('research.title')}
          />
          <div className="research-grid">
            <div className="background-stack">
              <div className="background-card">
                <span>Education</span>
                <strong>B.S. in Computer Science</strong>
                <p>Gonzaga University · Spokane, Washington</p>
              </div>
              <div className="background-card">
                <span>Research & teaching</span>
                <strong>Research Assistant and Teaching Assistant</strong>
                <p>Built an end-to-end machine-learning pipeline and supported iOS, Android, and data-science coursework.</p>
              </div>
            </div>

            <article className="publication-card">
              <p className="publication-meta">Selected publication · IEEE BHI · 2019</p>
              <h3>Utilizing Consumer-grade Wearable Sensors for Unobtrusive Rehabilitation Outcome Prediction</h3>
              <p>
                Co-authored research using consumer wearable data and machine learning to predict rehabilitation outcomes and support clinical planning.
              </p>
              <p className="publication-authors">Jason Conci, Gina Sprint, Diane J. Cook, Douglas Weeks</p>
              <a href="https://doi.org/10.1109/BHI.2019.8834512" target="_blank" rel="noopener noreferrer">
                View the publication <ArrowIcon />
              </a>
            </article>
          </div>

          <div className="skills-strip" aria-label="Technical skills and personal interests">
            <div>
              <span>Languages</span>
              <p>JavaScript · TypeScript · Python · SQL · C · C++</p>
            </div>
            <div>
              <span>Platforms</span>
              <p>Node.js · Vue · React · Electron · Express · Vite · AWS</p>
            </div>
            <div>
              <span>Interests</span>
              <p>Freshwater fishing · Automotive repair · Pottery · Live music</p>
            </div>
          </div>

          <section className="beyond-work" aria-labelledby="beyond-work-title">
            <div className="beyond-work-intro">
              <div>
                <p className="section-eyebrow">Outside the codebase</p>
                <h2 id="beyond-work-title">Beyond work.</h2>
              </div>
              <p>A few glimpses of the people, places, and pursuits that keep me curious.</p>
            </div>

            <div className="personal-gallery">
              <figure>
                <div className="personal-photo">
                  <img
                    src={fishingPhoto}
                    alt="Jason smiling in a boat while holding a freshly caught fish"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <span>On the water</span>
                  Freshwater fishing
                </figcaption>
              </figure>

              <figure>
                <div className="personal-photo">
                  <img
                    src={goosePhoto}
                    alt="Jason at home with his orange-and-white cat, Goose"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <span>At home</span>
                  With Goose
                </figcaption>
              </figure>

              <figure>
                <div className="personal-photo">
                  <img
                    src={barcelonaPhoto}
                    alt="Jason standing beneath a colorful mosaic arch in Barcelona"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <figcaption>
                  <span>Out exploring</span>
                  Barcelona
                </figcaption>
              </figure>
            </div>
          </section>
        </section>

        <section id="contact" className="contact-section" aria-labelledby="contact-title">
          <div>
            <p className="section-eyebrow">05. {t('contact.eyebrow')}</p>
            <h2 id="contact-title">{t('contact.title')}</h2>
            <p>{t('contact.description')}</p>
          </div>
          <a className="contact-link" href="mailto:jasonjconci@gmail.com">
            {t('contact.email')} <ArrowIcon />
          </a>
        </section>
      </main>

      <footer className="site-footer">
        <p>Designed and built by Jason Conci.</p>
        <nav aria-label="Footer navigation">
          <a href="https://github.com/jasonjconci" target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href="https://www.linkedin.com/in/jason-conci-82256b161/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="/Jason-Conci-Resume-Aug-2026.pdf" target="_blank" rel="noopener noreferrer">Résumé</a>
          <a href="/legacy">Legacy archive</a>
        </nav>
      </footer>
    </>
  );
}

export default Home;
